import { useEffect, useRef } from 'react';
import grapesjs, { Editor } from 'grapesjs';
import 'grapesjs/dist/css/grapes.min.css';
import grapesjsPresetWebpage from 'grapesjs-preset-webpage';
import grapesjsBlocksBasic from 'grapesjs-blocks-basic';
import grapesjsPluginForms from 'grapesjs-plugin-forms';
import grapesjsNavbar from 'grapesjs-navbar';
import grapesjsCustomCode from 'grapesjs-custom-code';
import grapesjsCountdown from 'grapesjs-component-countdown';
import grapesjsStyleGradient from 'grapesjs-style-gradient';
import grapesjsStyleFilter from 'grapesjs-style-filter';
import grapesjsBlocksFlexbox from 'grapesjs-blocks-flexbox';
import grapesjsTabs from 'grapesjs-tabs';
import grapesjsTooltip from 'grapesjs-tooltip';
import grapesjsTyped from 'grapesjs-typed';
import { supabase } from '../foundation/altzero_base/base/utils/supabaseClient';
import { useUser } from '../foundation/altzero_base/base/contextapi/UserContext';

export default function GrapesJSEditor() {
  const { user } = useUser();
  const editorRef = useRef<Editor | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Initialize GrapesJS editor
  useEffect(() => {
    if (!containerRef.current || editorRef.current) return;

    console.log('🎨 Initializing GrapesJS editor...');

    // Get template name from URL
    const params = new URLSearchParams(window.location.search);
    const templateName = params.get('template');

    const editor = grapesjs.init({
      container: containerRef.current,
      height: '100%',
      width: '100%',
      fromElement: false,
      storageManager: false, // Disable default storage
      
      // Plugins
      plugins: [
        grapesjsPresetWebpage,
        grapesjsBlocksBasic,
        grapesjsPluginForms,
        grapesjsNavbar,
        grapesjsCustomCode,
        grapesjsCountdown,
        grapesjsStyleGradient,
        grapesjsStyleFilter,
        grapesjsBlocksFlexbox,
        grapesjsTabs,
        grapesjsTooltip,
        grapesjsTyped,
      ],

      pluginsOpts: {
        [grapesjsPresetWebpage as any]: {
          // Preset options
        },
        [grapesjsCustomCode as any]: {
          // Custom code plugin options
          blockCustomCode: {
            label: 'Custom Code',
            category: 'Extra',
          },
          modalTitle: 'Insert your custom code',
          buttonLabel: 'Save',
        },
        [grapesjsCountdown as any]: {
          // Countdown component options
          startTime: '2025-12-31',
        },
        [grapesjsStyleGradient as any]: {
          // Gradient style options
        },
        [grapesjsStyleFilter as any]: {
          // Filter style options
        },
        [grapesjsBlocksFlexbox as any]: {
          // Flexbox blocks options
        },
        [grapesjsTabs as any]: {
          // Tabs component options
        },
        [grapesjsTooltip as any]: {
          // Tooltip component options
        },
        [grapesjsTyped as any]: {
          // Typed component options
        },
      },

      // Asset Manager configuration
      assetManager: {
        upload: false, // Disable default upload
      },

      // Canvas configuration
      canvas: {
        styles: [],
        scripts: [],
      },
    });

    editorRef.current = editor;

    // Add custom commands for save and load
    editor.Commands.add('save-template', {
      run: async (editor) => {
        try {
          console.log('💾 Save button clicked...');

          // Get template name from URL
          const params = new URLSearchParams(window.location.search);
          const templateName = params.get('template');

          if (!templateName) {
            console.error('❌ No template name found in URL');
            alert('Error: No template name found');
            return;
          }

          console.log('📝 Updating template:', templateName);

          const userId = user?.id || 'anonymous';
          const fileName = 'template.json';
          const filePath = `templates/${userId}/${templateName}/${fileName}`;

          // Get project data
          const projectData = {
            templateName: templateName,
            savedAt: new Date().toISOString(),
            userId: userId,
            project: {
              pages: editor.Pages.getAll().map(page => ({
                id: page.getId(),
                name: page.getName(),
                component: page.getMainComponent().toJSON(),
                styles: editor.getCss({ component: page.getMainComponent() }),
              })),
              styles: editor.getCss(),
              html: editor.getHtml(),
            }
          };

          const projectJson = JSON.stringify(projectData, null, 2);
          const blob = new Blob([projectJson], { type: 'application/json' });

          console.log('📁 Upload path:', filePath);
          console.log('📦 Template size:', `${(blob.size / 1024).toFixed(2)}KB`);

          // Remove old file first
          console.log('🗑️ Removing old file...');
          await supabase.storage
            .from('grapesjs_templates')
            .remove([filePath]);

          // Upload new file
          console.log('📤 Uploading new file...');
          const { error } = await supabase.storage
            .from('grapesjs_templates')
            .upload(filePath, blob, {
              cacheControl: '3600',
              upsert: false,
              contentType: 'application/json'
            });

          if (error) {
            console.error('❌ Upload error:', error);
            alert(`Upload failed: ${error.message}`);
            return;
          }

          console.log('✅ Template updated successfully!');
          alert('Template saved successfully!');
        } catch (error) {
          console.error('❌ Save error:', error);
          alert('Failed to save template');
        }
      }
    });

    // Add save button to the panel
    editor.Panels.addButton('options', {
      id: 'save-template',
      className: 'fa fa-floppy-o',
      command: 'save-template',
      attributes: { title: 'Save Template' },
    });

    // Load template if template name exists
    if (templateName) {
      loadTemplate(editor, templateName, user?.id || 'anonymous');
    }

    // Cleanup
    return () => {
      if (editorRef.current) {
        editorRef.current.destroy();
        editorRef.current = null;
      }
    };
  }, [user]);

  // Load template function
  const loadTemplate = async (editor: Editor, templateName: string, userId: string) => {
    try {
      console.log('📂 Loading template from Supabase...');
      console.log('📄 Template name:', templateName);

      const fileName = 'template.json';
      const filePath = `templates/${userId}/${templateName}/${fileName}`;

      // Get public URL with cache busting
      const { data: urlData } = supabase.storage
        .from('grapesjs_templates')
        .getPublicUrl(filePath);

      const cacheBuster = `?t=${Date.now()}&nocache=${Math.random()}`;
      const urlWithCacheBuster = urlData.publicUrl + cacheBuster;

      console.log('📥 Fetching template...');

      const response = await fetch(urlWithCacheBuster, {
        cache: 'no-store',
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      });

      if (!response.ok) {
        console.log('⚠️ Template not found, starting with empty canvas');
        return;
      }

      const fileContent = await response.text();
      const templateData = JSON.parse(fileContent);

      console.log('📦 Loaded template data:', {
        templateName: templateData.templateName,
        savedAt: templateData.savedAt,
      });

      // Load the project data
      if (templateData.project) {
        if (templateData.project.html) {
          editor.setComponents(templateData.project.html);
        }
        if (templateData.project.styles) {
          editor.setStyle(templateData.project.styles);
        }
      }

      console.log('✅ Template loaded successfully');
    } catch (error) {
      console.error('❌ Load error:', error);
    }
  };

  return (
    <div style={{ height: 'calc(100vh - 66px)', width: '100%', overflow: 'hidden' }}>
      <div ref={containerRef} style={{ height: '100%', width: '100%' }} />
    </div>
  );
}

